import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import {
  InAppNotificationPayload,
  GetNotificationsParams,
  NotificationStatsData,
  NotificationRecipientsResponse,
  InAppNotificationType,
  InAppNotification,
} from 'src/app/pages/update/models/in-app-notification';




@Injectable({
  providedIn: 'root',
})
export class InAppNotificationService {
  private inAppNotificationEndpoint: string = `${environment.baseUrl}${environment.accountsUrl}/in-app/notifications`;
  private recipientsCache$: Observable<NotificationRecipientsResponse> | null = null;

  private _stats$ = new BehaviorSubject<NotificationStatsData | null>(null);
  public stats$ = this._stats$.asObservable();

  constructor(private httpClient: HttpClient) {}

  refreshStats(): void {
    this.getUserInAppNotificationStats().subscribe({
      next: (res) => this._stats$.next(res.data),
      error: () => {},
    });
  }

  createInAppNotification(payload: InAppNotificationPayload): Observable<any> {
    return this.httpClient.post<any>(this.inAppNotificationEndpoint, payload);
  }

  getInAppNotifications(params?: GetNotificationsParams): Observable<InAppNotification> {
    let httpParams = new HttpParams();

    if (params) {
      if (params.page !== undefined) {
        httpParams = httpParams.set('page', params.page.toString());
      }
      if (params.pageSize !== undefined) {
        httpParams = httpParams.set('pageSize', params.pageSize.toString());
      }
      if (params.search) {
        httpParams = httpParams.set('search', params.search);
      }
      if (params.type) {
        httpParams = httpParams.set('type', params.type);
      }
      if (params.status) {
        httpParams = httpParams.set('status', params.status);
      }
      if (params.sortBy) {
        httpParams = httpParams.set('sortBy', params.sortBy);
      }
      if (params.order) {
        httpParams = httpParams.set('order', params.order);
      }
    }

    return this.httpClient.get<any>(this.inAppNotificationEndpoint, {
      params: httpParams,
    });
  }

  markAsRead(notificationId: number): Observable<any> {
    return this.httpClient.patch<any>(
      `${this.inAppNotificationEndpoint}/${notificationId}/read`,
      {}
    );
  }

  markAllAsReadWithChildren(notificationId: number){
    return this.httpClient.patch<any>(
      `${this.inAppNotificationEndpoint}/${notificationId}/children/read`,
      {}
    );
  }

  deleteInAppNotification(notificationId: number): Observable<any> {
    return this.httpClient.delete<any>(
      `${this.inAppNotificationEndpoint}/${notificationId}`
    );
  }

  updateInAppNotification(
    notificationId: number,
    payload: InAppNotificationPayload
  ): Observable<any> {
    return this.httpClient.put<any>(
      `${this.inAppNotificationEndpoint}/${notificationId}`,
      payload
    );
  }

  publishInAppNotification(notificationId: number): Observable<any> {
    return this.httpClient.post<any>(
      `${this.inAppNotificationEndpoint}/${notificationId}/publish`,
      {}
    );
  }

  unPublishInAppNotification(notificationId: number): Observable<any> {
    return this.httpClient.post<any>(
      `${this.inAppNotificationEndpoint}/${notificationId}/unpublish`,
      {}
    );
  }

  getDraftInAppNotifications(params?: GetNotificationsParams): Observable<InAppNotification> {
    let httpParams = new HttpParams();

    if (params) {
      if (params.page !== undefined) {
        httpParams = httpParams.set('page', params.page.toString());
      }
      if (params.pageSize !== undefined) {
        httpParams = httpParams.set('pageSize', params.pageSize.toString());
      }
      if (params.search) {
        httpParams = httpParams.set('search', params.search);
      }
      if (params.type) {
        httpParams = httpParams.set('type', params.type);
      }
      if (params.status) {
        httpParams = httpParams.set('status', params.status);
      }
      if (params.sortBy) {
        httpParams = httpParams.set('sortBy', params.sortBy);
      }
      if (params.order) {
        httpParams = httpParams.set('order', params.order);
      }
    }

    return this.httpClient.get<InAppNotification>(
      `${this.inAppNotificationEndpoint}/drafts`,
      { params: httpParams }
    );
  }


  getNotificationById(notificationId: number): Observable<InAppNotification> {
    return this.httpClient.get<InAppNotification>(
      `${this.inAppNotificationEndpoint}/${notificationId}`
    );
  }

  getUserInboxNotifications(
    userId: number,
    params?: GetNotificationsParams
  ): Observable<InAppNotification> {
    let httpParams = new HttpParams();

    if (params) {
      if (params.page !== undefined) {
        httpParams = httpParams.set('page', params.page.toString());
      }
      if (params.pageSize !== undefined) {
        httpParams = httpParams.set('pageSize', params.pageSize.toString());
      }
      if (params.search) {
        httpParams = httpParams.set('search', params.search);
      }
      if (params.type) {
        httpParams = httpParams.set('type', params.type);
      }
      if (params.status) {
        httpParams = httpParams.set('status', params.status);
      }
      if (params.sortBy) {
        httpParams = httpParams.set('sortBy', params.sortBy);
      }
      if (params.order) {
        httpParams = httpParams.set('order', params.order);
      }
    }

    return this.httpClient.get<InAppNotification>(
      `${this.inAppNotificationEndpoint}/user/${userId}`,
      { params: httpParams }
    );
  }

  getSentByMeNotifications(params?: GetNotificationsParams): Observable<InAppNotification> {
    let httpParams = new HttpParams();

    if (params) {
      if (params.page !== undefined) {
        httpParams = httpParams.set('page', params.page.toString());
      }
      if (params.pageSize !== undefined) {
        httpParams = httpParams.set('pageSize', params.pageSize.toString());
      }
      if (params.search) {
        httpParams = httpParams.set('search', params.search);
      }
      if (params.type) {
        httpParams = httpParams.set('type', params.type);
      }
      if (params.status) {
        httpParams = httpParams.set('status', params.status);
      }
      if (params.sortBy) {
        httpParams = httpParams.set('sortBy', params.sortBy);
      }
      if (params.order) {
        httpParams = httpParams.set('order', params.order);
      }
    }

    return this.httpClient.get<InAppNotification>(
      `${this.inAppNotificationEndpoint}/sent-by-me`,
      { params: httpParams }
    );
  }

  getScheduledNotifications(params?: GetNotificationsParams): Observable<InAppNotification> {
    let httpParams = new HttpParams();

    if (params) {
      if (params.page !== undefined) {
        httpParams = httpParams.set('page', params.page.toString());
      }
      if (params.pageSize !== undefined) {
        httpParams = httpParams.set('pageSize', params.pageSize.toString());
      }
      if (params.search) {
        httpParams = httpParams.set('search', params.search);
      }
      if (params.type) {
        httpParams = httpParams.set('type', params.type);
      }
      if (params.status) {
        httpParams = httpParams.set('status', params.status);
      }
      if (params.sortBy) {
        httpParams = httpParams.set('sortBy', params.sortBy);
      }
      if (params.order) {
        httpParams = httpParams.set('order', params.order);
      }
    }

    return this.httpClient.get<InAppNotification>(
      `${this.inAppNotificationEndpoint}/scheduled`,
      { params: httpParams }
    );
  }

  getUserInAppNotificationStats(): Observable<{ code: number; message: string; successful: boolean; data: NotificationStatsData }> {
    return this.httpClient.get<{ code: number; message: string; successful: boolean; data: NotificationStatsData }>(
      `${this.inAppNotificationEndpoint}/stats`
    );
  }

  // Get entire thread of notifications (parent + all children)
  getNotificationThread(notificationId: number): Observable<any> {
    return this.httpClient.get<any>(
      `${this.inAppNotificationEndpoint}/${notificationId}/thread`
    );
  }

  getRecipients(search = ''): Observable<NotificationRecipientsResponse> {
    if (search) {
      // Search queries are dynamic — always hit the API
      return this.httpClient.get<NotificationRecipientsResponse>(
        `${this.inAppNotificationEndpoint}/recipients/users-roles`,
        { params: new HttpParams().set('search', search) }
      );
    }

    // Initial full list — cache for the session so reopening compose doesn't refetch
    if (!this.recipientsCache$) {
      this.recipientsCache$ = this.httpClient.get<NotificationRecipientsResponse>(
        `${this.inAppNotificationEndpoint}/recipients/users-roles`
      ).pipe(shareReplay({ bufferSize: 1, refCount: true }));
    }
    return this.recipientsCache$;
  }

  clearRecipientsCache(): void {
    this.recipientsCache$ = null;
  }

  downloadImage(imageName: string): Observable<Blob> {
    return this.httpClient.get(
      `${environment.baseUrl}core/data/image`,
      { params: new HttpParams().set('imageName', imageName), responseType: 'blob' }
    );
  }

  // downloadDocument(documentName: string): Observable<Blob> {
  //   return this.httpClient.get(
  //     `${environment.baseUrl}core/data/document`,
  //     { params: new HttpParams().set('documentName', documentName), responseType: 'blob' }
  //   );
  // }

  downloadByRef(ref: string): Observable<Blob> {
    return this.httpClient.get(
      `${environment.baseUrl}core/data/${ref}`,
      { responseType: 'blob' }
    );
  }
}
